/**
 * FAL.ai Client Configuration
 * ============================
 * ONLY module that accesses the API key or proxy URL.
 * Single source of truth for FAL client setup.
 *
 * Modes:
 *   - Default: use VITE_FAL_PROXY_URL / VITE_FAL_PROXY_FALLBACK_URL so secrets stay server-side
 *   - Local escape hatch: VITE_FAL_API_KEY only when explicitly enabled for localhost dev
 */

import { fal } from '@fal-ai/client';
import {
  dispatchFalQueueSubmissionOnce,
  FAL_NON_REPLAYABLE_HEADER,
  FAL_NON_REPLAYABLE_VALUE,
} from './nonReplayableQueueTransport.js';
import {
  createFalProxyFetch,
  normalizeFalProxyUrl as normalizeProxyUrl,
} from './proxyFetchTransport.js';
import { createOnceOnlySubscribe } from './onceOnlySubscribe.js';

export const FAL_DEV_SERVER_PROXY_BASE_PATH = '/__glowghost_fal_proxy';
export { FAL_NON_REPLAYABLE_HEADER, FAL_NON_REPLAYABLE_VALUE };
export { createFalProxyFetch };

// API key - ONLY accessed here for security
const FAL_API_KEY = import.meta.env.VITE_FAL_API_KEY;
// Track if FAL client has been configured
let isConfigured = false;
let configuredTransportSignature = null;
const uploadAbortSignals = new WeakMap();

// Stored token getter for proxy auth
let _getAccessToken = null;

export function bindFalUploadAbortSignal(body, signal) {
  if (!(body instanceof Blob) || !signal) return () => {};
  uploadAbortSignals.set(body, signal);
  return () => uploadAbortSignals.delete(body);
}

export function createFalAbortAwareFetch(baseFetch = globalThis.fetch) {
  return (input, init) => {
    const uploadSignal = init?.body instanceof Blob
      ? uploadAbortSignals.get(init.body)
      : null;
    if (!uploadSignal || init?.signal) {
      return baseFetch(input, init);
    }
    return baseFetch(input, { ...init, signal: uploadSignal });
  };
}

function isLocalhostLikeHostname(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function isRemoteHttpProxyUrl(proxyUrl) {
  try {
    const parsed = new URL(proxyUrl);
    return ['http:', 'https:'].includes(parsed.protocol)
      && !isLocalhostLikeHostname(parsed.hostname);
  } catch {
    return false;
  }
}

function shouldUseFalDevServerProxy(env = import.meta.env) {
  return Boolean(env?.DEV && env?.VITE_FAL_USE_DEV_SERVER_PROXY !== 'false');
}

function resolveFalDevServerProxyUrl(proxyUrl, index, env = import.meta.env) {
  if (!shouldUseFalDevServerProxy(env) || !isRemoteHttpProxyUrl(proxyUrl)) {
    return proxyUrl;
  }

  return `${FAL_DEV_SERVER_PROXY_BASE_PATH}/${index}`;
}

function shouldAllowLocalDirectCredentials(
  env = import.meta.env,
  runtimeLocation = globalThis.window?.location,
) {
  return env?.DEV
    && Boolean(env?.VITE_FAL_API_KEY)
    && env?.VITE_ENABLE_LOCAL_DIRECT_AI === 'true'
    && isLocalhostLikeHostname(runtimeLocation?.hostname);
}

export function isLocalhostDevRuntime(
  env = import.meta.env,
  runtimeLocation = globalThis.window?.location,
) {
  return Boolean(env?.DEV && isLocalhostLikeHostname(runtimeLocation?.hostname));
}

export function resolveFalProxyUrls(
  env = import.meta.env,
) {
  const configuredPrimaryProxyUrl = normalizeProxyUrl(env?.VITE_FAL_PROXY_URL);
  const configuredFallbackProxyUrl = normalizeProxyUrl(env?.VITE_FAL_PROXY_FALLBACK_URL);

  const configuredProxyUrls = [
    configuredPrimaryProxyUrl,
    configuredFallbackProxyUrl,
  ]
    .filter(Boolean)
    .filter((url, index, list) => list.indexOf(url) === index);

  return configuredProxyUrls.map((proxyUrl, index) => (
    resolveFalDevServerProxyUrl(proxyUrl, index, env)
  ));
}

export function getFalProxyUrls() {
  return resolveFalProxyUrls(import.meta.env);
}

export function getFalProxyAccessToken() {
  return typeof _getAccessToken === 'function'
    ? _getAccessToken()
    : null;
}

/**
 * Submit one paid FAL queue request with no client/proxy replay.
 *
 * The regular SDK queue transport retries retryable HTTP responses, and the
 * shared proxy transport can switch targets. That is appropriate for reads but
 * unsafe for paid queue submissions without provider idempotency. This boundary
 * sends exactly one request to exactly one target; an ambiguous transport
 * failure must be reconciled by the owning operation instead of resubmitted.
 */
export async function submitFalQueueRequestOnce(
  endpointId,
  input,
  {
    signal,
    baseFetch = globalThis.fetch,
    proxyUrls = getFalProxyUrls(),
    accessToken = getFalProxyAccessToken(),
    directCredentials = FAL_API_KEY,
    onDispatch,
    allowDirectCredentials = shouldAllowLocalDirectCredentials(
      import.meta.env,
      globalThis.window?.location,
    ),
  } = {},
) {
  return dispatchFalQueueSubmissionOnce({
    endpointId,
    input,
    signal,
    baseFetch,
    primaryProxyUrl: proxyUrls[0] || null,
    accessToken,
    directCredentials,
    allowDirectCredentials,
    onDispatch,
  });
}

function buildProxyConfigurationError(proxyUrls, error, runtimeLocation = globalThis.window?.location) {
  const hostname = runtimeLocation?.hostname || '';
  const isLocalDev = isLocalhostLikeHostname(hostname);
  const errorMessage = String(error?.message || '');

  if (isLocalDev && (error instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(errorMessage))) {
    const attemptedTargets = proxyUrls.length > 0 ? proxyUrls.join(', ') : 'configured FAL proxies';
    return new Error(
      `FAL proxy request failed from localhost. The configured proxy targets (${attemptedTargets}) rejected or blocked the browser request, likely due to CORS or an unavailable worker. ` +
      'Set VITE_FAL_PROXY_URL to a reachable worker URL and verify that worker allows this origin. ' +
      'For localhost-only debugging, you can opt into direct browser credentials with VITE_ENABLE_LOCAL_DIRECT_AI=true.',
      { cause: error },
    );
  }

  return error;
}

export function createConfiguredFalProxyFetch(
  proxyUrls = getFalProxyUrls(),
  baseFetch = globalThis.fetch,
  runtimeLocation = globalThis.window?.location,
) {
  const proxyFetch = createFalProxyFetch(proxyUrls, baseFetch);

  return async (input, init) => {
    try {
      return await proxyFetch(input, init);
    } catch (error) {
      throw buildProxyConfigurationError(proxyUrls, error, runtimeLocation);
    }
  };
}

function buildFalTransportSignature({
  proxyUrls = [],
  usesDirectCredentials = false,
  hasAccessTokenGetter = false,
}) {
  return JSON.stringify({
    proxyUrls,
    usesDirectCredentials,
    hasAccessTokenGetter,
  });
}

/**
 * Initialize FAL client (lazy, one-time setup)
 * @param {Function} [getAccessToken] - Returns Supabase JWT for proxy auth
 * @returns {boolean} True if configured successfully
 */
export function initializeFalClient(getAccessToken) {
  if (getAccessToken) _getAccessToken = getAccessToken;

  try {
    const proxyUrls = getFalProxyUrls();
    const usesDirectCredentials = proxyUrls.length === 0
      && shouldAllowLocalDirectCredentials(import.meta.env, globalThis.window?.location);
    const nextTransportSignature = buildFalTransportSignature({
      proxyUrls,
      usesDirectCredentials,
      hasAccessTokenGetter: Boolean(_getAccessToken),
    });

    if (isConfigured && configuredTransportSignature === nextTransportSignature) {
      return true;
    }

    if (proxyUrls.length > 0) {
      // Production: route through Worker proxy
      const abortAwareFetch = createFalAbortAwareFetch(globalThis.fetch);
      const config = {
        proxyUrl: proxyUrls[0],
        fetch: createConfiguredFalProxyFetch(
          proxyUrls,
          abortAwareFetch,
          globalThis.window?.location,
        ),
      };

      if (_getAccessToken) {
        config.requestMiddleware = async (request) => {
          const token = _getAccessToken();
          if (token) {
            request.headers = {
              ...request.headers,
              'Authorization': `Bearer ${token}`
            };
          }
          return request;
        };
      }

      fal.config(config);
      isConfigured = true;
      configuredTransportSignature = nextTransportSignature;
      return true;
    } else if (usesDirectCredentials) {
      // Explicit localhost-only escape hatch for solo debugging outside the worker path.
      fal.config({
        credentials: FAL_API_KEY,
        fetch: createFalAbortAwareFetch(globalThis.fetch),
      });
      isConfigured = true;
      configuredTransportSignature = nextTransportSignature;
      return true;
    } else {
      console.warn(
        '⚠️ No FAL proxy transport configured. Set VITE_FAL_PROXY_URL, or for localhost-only debugging set both VITE_FAL_API_KEY and VITE_ENABLE_LOCAL_DIRECT_AI=true.',
      );
      isConfigured = false;
      configuredTransportSignature = null;
      return false;
    }
  } catch (error) {
    console.error('❌ FAL client configuration failed:', error.message);
    isConfigured = false;
    configuredTransportSignature = null;
    return false;
  }
}

/**
 * Check if API key or proxy URL is available
 * @returns {boolean}
 */
export function hasApiKey() {
  return shouldAllowLocalDirectCredentials(import.meta.env, globalThis.window?.location)
    || getFalProxyUrls().length > 0;
}

/**
 * Get the configured FAL client
 * Ensures client is initialized before returning
 * @returns {Object} FAL client instance
 */
export function getFalClient() {
  initializeFalClient();
  onceOnlyFalClient ??= createOnceOnlyFalClient();
  return onceOnlyFalClient;
}

// Every subscribe through the shared client is a single paid POST; see onceOnlySubscribe.js.
// Built on first use: onceOnlySubscribe.js reaches back into this module through the
// once-only submit, so it cannot be called while this module is still evaluating.
let onceOnlyFalClient = null;

function createOnceOnlyFalClient() {
  const onceOnlySubscribe = createOnceOnlySubscribe(fal);
  return new Proxy(fal, {
    get(target, property, receiver) {
      if (property === 'subscribe') return onceOnlySubscribe;
      return Reflect.get(target, property, receiver);
    },
  });
}

/**
 * Check if client is ready to use
 * @returns {boolean}
 */
export function isClientReady() {
  return isConfigured && hasApiKey();
}

// Export FAL client for direct use (if needed)
export { fal };
